import { getStoredAnalyticsConsent, isEEARegion } from './firebase';

// ============================================================
// OpenAI / ChatGPT Ads measurement pixel ("oaiq")
// ============================================================
// Docs: https://developers.openai.com/ads/measurement-pixel
//
// The loader lives here rather than in index.html on purpose. The SDK drops
// the __oppref (30d) and __obref (365d) attribution cookies the instant it
// initialises, so it has to sit behind the same consent gate as Firebase
// Analytics: non-EEA visitors are auto-granted, EEA/UK/Swiss visitors get the
// pixel only after they click Accept in CookieNotice. main.tsx calls
// initOaiq() on boot, so the cost of not being in <head> is one bundle parse -
// the URL (and therefore ?oppref=) is still intact when we init.
//
// Two rules the OpenAI docs are easy to miss:
//   - Amounts are in the currency's MINOR unit. $49 is 4900, not 49.
//   - page_viewed is NOT automatic. Every page view is a manual measure call,
//     which for an SPA means firing on route change (see App.tsx).

const PIXEL_ID = 'XxFAvDck82qTktPitXrPBo';
const SDK_URL = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const DEBUG_STORAGE_KEY = 'romulus_oaiq_debug';

type OaiqFn = { (...args: unknown[]): void; q?: IArguments[] };

const getWindow = (): (Window & { oaiq?: OaiqFn }) | null =>
  typeof window === 'undefined' ? null : (window as Window & { oaiq?: OaiqFn });

// Same rule initAnalytics() applies: an explicit stored choice wins, otherwise
// EEA defaults to denied and everyone else to granted.
const consentGranted = (): boolean =>
  (getStoredAnalyticsConsent() ?? (isEEARegion() ? 'denied' : 'granted')) === 'granted';

// `?oaiq_debug=1` turns on the SDK's console logging AND sticks, so it survives
// the hop out to Stripe and back into /register - otherwise the one event worth
// verifying (order_created) is the one you can never watch.
const debugEnabled = (): boolean => {
  const w = getWindow();
  if (!w) return false;
  try {
    if (new URLSearchParams(w.location.search).get('oaiq_debug') === '1') {
      localStorage.setItem(DEBUG_STORAGE_KEY, '1');
      return true;
    }
    return localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

// The vendor snippet, transcribed. Pushes the real `arguments` object (not a
// copied array) because that is what the SDK replays off the queue.
const loadSdk = (): void => {
  const w = getWindow();
  if (!w || w.oaiq) return;
  const q: OaiqFn = function () {
    // eslint-disable-next-line prefer-rest-params
    q.q!.push(arguments);
  } as OaiqFn;
  q.q = [];
  w.oaiq = q;
  const script = w.document.createElement('script');
  script.async = true;
  script.src = SDK_URL;
  const first = w.document.getElementsByTagName('script')[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else w.document.head.appendChild(script);
};

let initialised = false;
let hashedEmail: string | null = null;

export const initOaiq = (): void => {
  if (initialised) return;
  const w = getWindow();
  if (!w) return;
  const debug = debugEnabled();
  // Local/emulator traffic must not land in the ad account. `?oaiq_debug=1`
  // is the deliberate override for testing the wiring against the real pixel.
  if (!debug && (import.meta.env.DEV || import.meta.env.VITE_USE_EMULATORS === 'true')) return;
  if (!consentGranted()) return;
  try {
    loadSdk();
    w.oaiq?.('init', {
      pixelId: PIXEL_ID,
      ...(debug ? { debug: true } : {}),
      ...(hashedEmail ? { user: { email_sha256: hashedEmail } } : {}),
    });
    initialised = true;
  } catch (err) {
    console.warn('[oaiq] init failed', err);
  }
};

type ContentsItem = {
  id?: string;
  name?: string;
  content_type?: 'product' | 'plan' | 'page' | string;
  quantity?: number;
  amount?: number;
  currency?: string;
};

type EventData =
  | { type: 'contents'; amount?: number; currency?: string; contents?: ContentsItem[] }
  | { type: 'customer_action'; amount?: number; currency?: string }
  | { type: 'plan_enrollment'; plan_id?: string; amount?: number; currency?: string };

type EventOptions = { event_id?: string; custom_event_name?: string; opt_out?: boolean };

// Every conversion we send carries an event_id so a duplicate - the same
// purchase seen by both /checkout and /register, or later by a server-side
// Conversions API call - collapses into one conversion instead of two.
const sentEventIds = new Set<string>();

const measure = (event: string, data: EventData, options?: EventOptions): void => {
  try {
    initOaiq();
    if (!initialised) return;
    const id = options?.event_id;
    if (id) {
      if (sentEventIds.has(id)) return;
      sentEventIds.add(id);
    }
    const w = getWindow();
    if (options) w?.oaiq?.('measure', event, data, options);
    else w?.oaiq?.('measure', event, data);
  } catch (err) {
    console.warn('[oaiq] measure failed', err);
  }
};

/** Manual page view - the SDK never sends one on its own. */
export const oaiqPageView = (path?: string): void => {
  const w = getWindow();
  measure('page_viewed', {
    type: 'contents',
    contents: [
      {
        id: path ?? w?.location.pathname ?? '/',
        content_type: 'page',
        quantity: 1,
      },
    ],
  });
};

/** Fired when the buyer reaches a real checkout - embedded form or hosted redirect. */
export const oaiqCheckoutStarted = (
  planId: string,
  planName: string,
  amountCents: number,
): void => {
  measure('checkout_started', {
    type: 'contents',
    amount: amountCents,
    currency: 'USD',
    contents: [
      { id: planId, name: planName, content_type: 'plan', quantity: 1, amount: amountCents },
    ],
  });
};

/**
 * The money event. `eventId` must be stable for a given purchase across every
 * place that can observe it (Stripe subscription id, payment intent id, or
 * checkout session id - never a random uuid).
 */
export const oaiqOrderCreated = (args: {
  eventId: string;
  planId: string;
  planName: string;
  amountCents: number;
}): void => {
  measure(
    'order_created',
    {
      type: 'contents',
      amount: args.amountCents,
      currency: 'USD',
      contents: [
        {
          id: args.planId,
          name: args.planName,
          content_type: 'plan',
          quantity: 1,
          amount: args.amountCents,
        },
      ],
    },
    { event_id: args.eventId },
  );
};

/**
 * Advanced matching. Hashing the email the buyer typed at checkout is what
 * lets OpenAI tie the conversion back to the click when cookies don't survive
 * (in-app browsers, ITP, the Stripe round trip). Normalised then SHA-256 hex,
 * exactly as the pixel expects - the raw address never leaves the page.
 */
export const setOaiqUserEmail = async (email: string): Promise<void> => {
  try {
    const normalised = email.trim().toLowerCase();
    if (!normalised || !window.crypto?.subtle) return;
    const bytes = new TextEncoder().encode(normalised);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    if (hex === hashedEmail) return;
    hashedEmail = hex;
    // Docs: call init again once user data is available.
    if (initialised) {
      getWindow()?.oaiq?.('init', {
        pixelId: PIXEL_ID,
        ...(debugEnabled() ? { debug: true } : {}),
        user: { email_sha256: hex },
      });
    } else {
      initOaiq();
    }
  } catch (err) {
    console.warn('[oaiq] user match failed', err);
  }
};
